import { effect, inject, Component, Input, type OnChanges, type SimpleChanges, ViewChild } from '@angular/core';
import { type ChartConfiguration, type ChartType } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';

import { ThemeService } from '../../services/theme.service';
import { applyChartThemeDefaults, applyChartThemeToChart, CHART_DATA } from '../../utilities/chart-theme.util';

@Component({
  selector: 'app-bar-chart',
  standalone: false,
  templateUrl: './bar-chart.component.html',
  styleUrl: './bar-chart.component.scss'
})
export class BarChartComponent implements OnChanges {
  @Input() title: string = 'Bar Chart';
  @Input() labels: string[] = [];
  @Input() data: number[] = [];
  @Input() datasets: { label: string; data: number[]; backgroundColor?: string; borderColor?: string }[] = [];
  @Input() xAxisLabel: string = '';
  @Input() yAxisLabel: string = '';
  @Input() integerYAxis: boolean = false; // New input to control integer-only y-axis

  @ViewChild(BaseChartDirective) chart?: BaseChartDirective;

  private readonly themeService = inject(ThemeService);

  constructor() {
    // Re-theme chart.js ink (text + gridlines) whenever the light/dark theme changes.
    effect(() => {
      const isDark = this.themeService.isDark();
      applyChartThemeDefaults(isDark);
      applyChartThemeToChart(this.chart?.chart, isDark);
      this.chart?.update();
    });
  }

  public barChartType: ChartType = 'bar';
  public barChartData: ChartConfiguration<'bar'>['data'] = {
    labels: [],
    datasets: [
      {
        data: [],
        backgroundColor: CHART_DATA.primaryFill,
        borderColor: CHART_DATA.primaryBorder,
        borderWidth: 1
      }
    ]
  };

  public barChartOptions: ChartConfiguration<'bar'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top'
      },
      title: {
        display: false
      }
    },
    scales: {
      x: {
        title: {
          display: true,
          text: this.xAxisLabel
        }
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: this.yAxisLabel
        },
        ticks: {
          callback: function (tickValue: string | number) {
            // Only show integer values
            if (typeof tickValue === 'number' && Number.isInteger(tickValue)) {
              return tickValue;
            }
            return null;
          }
        }
      }
    }
  };

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['labels'] || changes['data'] || changes['datasets']) {
      this.barChartData.labels = [...this.labels];

      // If datasets input is provided, use it; otherwise use the single data array
      if (this.datasets && this.datasets.length > 0) {
        this.barChartData.datasets = this.datasets.map(ds => ({
          label: ds.label,
          data: [...ds.data],
          backgroundColor: ds.backgroundColor || CHART_DATA.primaryFill,
          borderColor: ds.borderColor || CHART_DATA.primaryBorder,
          borderWidth: 1
        }));
        // Show legend for multiple datasets
        if (this.barChartOptions?.plugins?.legend) {
          this.barChartOptions.plugins.legend.display = true;
        }
      } else {
        this.barChartData.datasets = [
          {
            label: this.title, // Use chart title as dataset label
            data: [...this.data],
            backgroundColor: CHART_DATA.primaryFill,
            borderColor: CHART_DATA.primaryBorder,
            borderWidth: 1
          }
        ];
        // Hide legend for single dataset
        if (this.barChartOptions?.plugins?.legend) {
          this.barChartOptions.plugins.legend.display = false;
        }
      }

      this.chart?.update();
    }

    if (changes['xAxisLabel'] || changes['yAxisLabel'] || changes['integerYAxis']) {
      if (this.barChartOptions?.scales) {
        if (this.barChartOptions.scales['x']?.title) {
          this.barChartOptions.scales['x'].title.text = this.xAxisLabel;
        }
        if (this.barChartOptions.scales['y']) {
          if (this.barChartOptions.scales['y'].title) {
            this.barChartOptions.scales['y'].title.text = this.yAxisLabel;
          }
          // Configure y-axis ticks based on integerYAxis input
          if (this.integerYAxis) {
            this.barChartOptions.scales['y'].ticks = {
              callback: function (tickValue: string | number) {
                // Only show integer values
                if (typeof tickValue === 'number' && Number.isInteger(tickValue)) {
                  return tickValue;
                }
                return null;
              }
            };
          } else {
            // Reset to default ticks if integerYAxis is false
            this.barChartOptions.scales['y'].ticks = {};
          }
        }
      }
      this.chart?.update();
    }
  }
}
